import {
  ChangesResponse,
  HeadResponse,
  MembershipsResponse,
  PushResponse,
  SnapshotResponse,
  type EntityType,
  type PushRequest,
} from '@fd/schemas';
import { TransportError, type SyncTransport } from '@fd/sync-engine';
import type { z } from 'zod';

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type HttpTransportOptions = {
  /** Base URL of the sync function, e.g. https://<ref>.supabase.co/functions/v1/sync */
  readonly baseUrl: string;
  readonly getAccessToken: () => Promise<string | null>;
  /** Extra headers (Supabase gateway wants `apikey`). */
  readonly headers?: Record<string, string>;
  readonly fetch?: FetchLike;
  readonly timeoutMs?: number;
  readonly deviceId?: string;
};

const newCorrelationId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Typed HTTP client for the `sync` Edge Function; responses are validated with the shared schemas. */
export function createHttpTransport(options: HttpTransportOptions): SyncTransport & {
  memberships(): Promise<z.infer<typeof MembershipsResponse>>;
} {
  const doFetch: FetchLike = options.fetch ?? ((input, init) => fetch(input, init));
  const base = options.baseUrl.replace(/\/$/, '');

  async function call<T>(method: 'GET' | 'POST', path: string, schema: z.ZodType<T> | null, body?: unknown): Promise<T> {
    const token = await options.getAccessToken();
    if (!token) throw new TransportError('Not signed in', { status: 401, code: 'UNAUTHENTICATED', retryable: false });
    const correlationId = newCorrelationId();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs ?? 30_000) : null;
    let response: Response;
    try {
      response = await doFetch(`${base}${path}`, {
        method,
        headers: {
          authorization: `Bearer ${token}`,
          'content-type': 'application/json',
          'x-correlation-id': correlationId,
          ...(options.deviceId ? { 'x-device-id': options.deviceId } : {}),
          ...options.headers,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller?.signal,
      });
    } catch (error) {
      throw new TransportError(error instanceof Error ? error.message : 'Network error', {
        status: null,
        code: 'NETWORK',
        retryable: true,
        correlationId,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }
    const text = await response.text();
    const payload = text ? (JSON.parse(text) as unknown) : null;
    if (!response.ok) {
      const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new TransportError(error?.message ?? `HTTP ${response.status}`, {
        status: response.status,
        code: error?.code ?? `HTTP_${response.status}`,
        retryable: response.status >= 500 || response.status === 429 || response.status === 408,
        correlationId,
      });
    }
    return schema ? schema.parse(payload) : (payload as T);
  }

  const query = (params: Record<string, string | number | null | undefined>) =>
    '?' +
    Object.entries(params)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');

  return {
    push: (request: PushRequest) => call('POST', '/push', PushResponse, request),
    changes: (householdId, cursor, limit) => call('GET', `/changes${query({ householdId, cursor, limit })}`, ChangesResponse),
    head: (householdId) => call('GET', `/head${query({ householdId })}`, HeadResponse),
    snapshot: (householdId, entityType: EntityType, after, limit, since) =>
      call('GET', `/snapshot${query({ householdId, entityType, after, limit, since })}`, SnapshotResponse),
    resolveConflict: async (householdId, conflictId, resolution, operationId) => {
      await call('POST', `/conflicts/${conflictId}/resolve${query({ householdId })}`, null, { resolution, operationId });
    },
    memberships: () => call('GET', '/memberships', MembershipsResponse),
  };
}
